import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";

/**
 * Anti-download / anti-screenshot image with:
 * - Disabled context menu, drag, selection
 * - Invisible transparent overlay that intercepts drag/save clicks
 * - CSS-drawn watermark with user email + timestamp (deters screenshot sharing)
 * - Auto-blur when tab loses focus (screenshot apps often switch focus)
 * - Image served with `draggable={false}` + CSS `pointer-events:none`
 *
 * NOTE: No browser API can truly block OS-level screenshots; this is the strongest
 * realistic deterrent combination and forensic traceback via watermark.
 */
export default function ProtectedImage({
  src,
  alt = "",
  className = "",
  style = {},
  showWatermark = true,
  onClick,
  testid,
}) {
  const { user } = useAuth();
  const wrapRef = useRef(null);
  const [blur, setBlur] = useState(false);

  useEffect(() => {
    const onVis = () => setBlur(document.hidden);
    const onBlurEvt = () => setBlur(true);
    const onFocusEvt = () => setBlur(false);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlurEvt);
    window.addEventListener("focus", onFocusEvt);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlurEvt);
      window.removeEventListener("focus", onFocusEvt);
    };
  }, []);

  const noAction = (e) => { e.preventDefault(); e.stopPropagation(); return false; };

  // Watermark id: prefer email, fallback to first 8 chars of user id, else "guest"
  const wmId = user?.email || (user?.id ? user.id.slice(0, 8) : "guest");
  const wmStamp = new Date().toLocaleDateString("es-MX");

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        ...style,
      }}
      onContextMenu={noAction}
      onDragStart={noAction}
      onClick={onClick}
      data-testid={testid}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover select-none pointer-events-none"
        style={{
          filter: blur ? "blur(24px)" : "none",
          transition: "filter 0.2s",
          WebkitUserDrag: "none",
        }}
        draggable={false}
        onDragStart={noAction}
        onContextMenu={noAction}
        loading="lazy"
      />

      {/* Repeating diagonal watermark — invisible-ish (low alpha) but visible in screenshots */}
      {showWatermark && wmId && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent 0px,
              transparent 180px,
              rgba(255,255,255,0.05) 180px,
              rgba(255,255,255,0.05) 182px
            )`,
          }}
        >
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              transform: "rotate(-18deg)",
              opacity: 0.11,
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
              color: "#fff",
              textShadow: "0 0 4px rgba(0,0,0,0.5)",
              fontWeight: 600,
              letterSpacing: "0.08em",
            }}
          >
            <div className="grid grid-cols-2 gap-x-10 gap-y-10 whitespace-nowrap">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i}>PK2 SCARLETT · {wmId} · {wmStamp}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transparent intercept layer — captures drag attempts that would otherwise
          reach the <img>. Also disables accidental text selection. */}
      <div
        className="absolute inset-0"
        style={{ background: "transparent", cursor: onClick ? "zoom-in" : "default" }}
        onContextMenu={noAction}
        onDragStart={noAction}
        onClick={onClick}
      />

      {blur && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{
            background: "rgba(10,10,10,0.7)",
            color: "var(--brand-primary)",
            fontFamily: "serif",
            fontSize: 14,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          Contenido protegido
        </div>
      )}
    </div>
  );
}
